import React, { useState, useEffect, useRef } from 'react';
import { extractDataFromDocument } from '../services/ocrService';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from '../utils';

export default function CasesView({ cases, clients, firmUsers, onOpenNetWizard, onCaseSelect, dbOps, globalSearch, prefilledClient, clearPrefilledClient }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [formData, setFormData] = useState({ id: '', title: '', client: '', opposingParties: '', court: '', city: '', type: 'כללי', status: 'פעיל', deadline: '', lawyer: '' });
  const [isOcrLoading, setIsOcrLoading] = useState(false);

  useEffect(() => {
    if (prefilledClient) {
      openNewModal();
      setFormData(prev => ({ ...prev, client: prefilledClient }));
      clearPrefilledClient();
    }
  }, [prefilledClient]);

  const handleSaveCase = (e) => {
    e.preventDefault();
    if (editingCase) {
      dbOps.handleSave('cases', formData, editingCase._id);
    } else {
      dbOps.handleSave('cases', formData);
    }
    setIsModalOpen(false);
    setEditingCase(null);
  };

  const openNewModal = () => {
    setEditingCase(null);
    setFormData({ id: '', title: '', client: '', opposingParties: '', court: '', city: '', type: 'כללי', status: 'פעיל', deadline: '', lawyer: '' });
    setIsModalOpen(true);
  };

  const openEditModal = (c, e) => {
    e.stopPropagation();
    setEditingCase(c);
    setFormData({ id: c.id || '', title: c.title || '', client: c.client || '', opposingParties: c.opposingParties || '', court: c.court || '', city: c.city || '', type: c.type || 'כללי', status: c.status || 'פעיל', deadline: c.deadline || '', lawyer: c.lawyer || '' });
    setIsModalOpen(true);
  };

  const handleOcrUpload = async (e) => {
    const file = e.target.files?.[0] || (e.dataTransfer && e.dataTransfer.files?.[0]);
    if (!file) return;
    setIsOcrLoading(true);
    
    const extractedData = await extractDataFromDocument(file, 'case');
    
    // --- אוטומציה לזיהוי לקוח קיים מתוך המסמך ---
    if (extractedData.foundPlaintiff || extractedData.opposingParties) {
      const textToSearch = `${extractedData.foundPlaintiff} ${extractedData.opposingParties}`;
      
      // חיפוש הלקוח במאגר הלקוחות שלנו
      const matchedClient = clients.find(c => textToSearch.includes(c.name));
      
      if (matchedClient) {
        extractedData.client = matchedClient.name;
      }
    }

    if (!extractedData.id && !extractedData.title) {
      alert("לא הצלחנו לפענח שדות בצורה אוטומטית.\n\nהטקסט שזוהה (לצורכי בדיקה):\n" + (extractedData.rawText || "לא זוהה טקסט"));
    }

    if (Object.keys(extractedData).length > 0) {
      setFormData(prev => ({ ...prev, ...extractedData }));
    }
    
    setIsOcrLoading(false);
  };

  const filteredCases = cases.filter(c => 
    (c.title || '').includes(globalSearch || '') || 
    (c.id || '').includes(globalSearch || '') || 
    (c.client || '').includes(globalSearch || '') ||
    (c.opposingParties || '').includes(globalSearch || '')
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full">
      <div className="p-4 md:p-6 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between bg-slate-50/50 gap-4">
        <h3 className="font-bold text-lg text-slate-800">תיקים משפטיים</h3>
        <button onClick={openNewModal} className="w-full sm:w-auto bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
          <Plus className="w-4 h-4" /> פתיחת תיק חדש
        </button>
      </div>
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-right whitespace-nowrap min-w-[800px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-sm">
            <tr>
              <th className="p-4 font-medium">מספר/שם תיק</th><th className="p-4 font-medium">תיאור</th><th className="p-4 font-medium">לקוח</th><th className="p-4 font-medium">סוג משפטי</th><th className="p-4 font-medium">סטטוס</th><th className="p-4 font-medium">יעד לקרוב</th><th className="p-4 font-medium text-center">נט המשפט</th><th className="p-4 font-medium">פעולות</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {filteredCases.length === 0 ? <tr><td colSpan="8" className="p-8 text-center text-slate-400">לא נמצאו תיקים תואמים.</td></tr> : 
             filteredCases.map((c) => (
              <tr key={c._id} className="hover:bg-blue-50/30 transition-colors group cursor-pointer" onClick={() => onCaseSelect(c)}>
                <td className="p-4 font-semibold text-blue-700"><span className="hover:underline transition-all truncate max-w-[150px] inline-block">{c.id || 'ללא מספר'}</span></td>
                <td className="p-4 font-medium text-slate-800 truncate max-w-[200px]">{c.title}</td>
                <td className="p-4 text-slate-600">{c.client}</td>
                <td className="p-4 text-slate-600">{c.type}</td>
                <td className="p-4"><span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${c.status === 'פעיל' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : c.status === 'במו"מ' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{c.status}</span></td>
                <td className="p-4 text-slate-600"><div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" /> {formatHebDate(c.deadline)}</div></td>
                <td className="p-4 text-center"><button onClick={(e) => { e.stopPropagation(); onOpenNetWizard(c); }} className="bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-200 px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors mx-auto" title="הכנה לנט המשפט"><Layers className="w-3.5 h-3.5" /> הכנה</button></td>
                <td className="p-4 flex items-center gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => openEditModal(c, e)} className="text-slate-400 hover:text-blue-600 transition-colors p-1" title="ערוך תיק"><Edit className="w-4 h-4" /></button>
                  <button onClick={(e) => { e.stopPropagation(); dbOps.handleDelete('cases', c._id); }} className="text-slate-400 hover:text-red-600 transition-colors p-1" title="מחק תיק"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" dir="rtl">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl overflow-hidden animate-in fade-in zoom-in duration-200 max-h-[95vh] flex flex-col">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center shrink-0">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><Briefcase className="w-5 h-5 text-blue-600" /> {editingCase ? 'עריכת פרטי תיק' : 'פתיחת תיק חדש'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="flex flex-col md:flex-row flex-1 overflow-y-auto">
               {/* אזור גרירת מסמך לזיהוי אוטומטי בתיק */}
               {!editingCase && (
                 <div className="w-full md:w-1/3 bg-slate-50 p-6 border-b md:border-b-0 md:border-l border-slate-200 flex flex-col items-center justify-center shrink-0">
                   <div className="w-full h-48 md:h-full border-2 border-dashed border-indigo-300 rounded-xl flex flex-col items-center justify-center bg-white hover:bg-indigo-50 transition-colors relative cursor-pointer group"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); handleOcrUpload(e); }}>
                     {isOcrLoading ? (
                       <div className="flex flex-col items-center">
                         <RefreshCw className="w-10 h-10 text-indigo-500 animate-spin mb-3"/>
                         <p className="text-sm font-medium text-slate-700">סורק נתונים מהמסמך...</p>
                       </div>
                     ) : (
                       <>
                         <FileText className="w-10 h-10 text-indigo-400 group-hover:text-indigo-600 transition-colors mb-3" />
                         <h4 className="font-bold text-slate-800 text-sm mb-1">פענוח אוטומטי מכתב בית דין</h4>
                         <p className="text-xs text-slate-500 text-center px-4 mb-4">גרור לכאן מסמך או לחץ לבחירה למילוי אוטומטי של פרטי התיק</p>
                         <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={handleOcrUpload} accept=".pdf,image/*" />
                       </>
                     )}
                   </div>
                 </div>
               )}

              <form onSubmit={handleSaveCase} className="p-4 sm:p-6 space-y-4 flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">מספר הליך (לדוגמה: ת.א 1234-01-26) <span className="text-red-500">*</span></label>
                    <input required type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.id} onChange={e => setFormData({...formData, id: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">לקוח מיוצג <span className="text-red-500">*</span></label>
                    <select required className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 bg-white transition-all" value={formData.client} onChange={e => setFormData({...formData, client: e.target.value})}>
                      <option value="" disabled>-- בחר לקוח --</option>
                      {clients.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">מהות התיק / תיאור קצר <span className="text-red-500">*</span></label>
                    <input required type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-slate-700 mb-1">צדדים מנגד</label>
                    <input type="text" placeholder="למשל: דוד כהן ואח' - ע&quot;י עו&quot;ד יצחק לוי" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.opposingParties} onChange={e => setFormData({...formData, opposingParties: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">בית משפט / דין</label>
                    <input type="text" placeholder="למשל: בית משפט השלום" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.court} onChange={e => setFormData({...formData, court: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">עיר / מחוז</label>
                    <input type="text" placeholder="למשל: תל אביב" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">סוג משפטי</label>
                    <input type="text" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">סטטוס</label>
                    <select className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 bg-white transition-all" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                      <option value="פעיל">פעיל</option>
                      <option value="במו&quot;מ">במו"מ</option>
                      <option value="ממתין להחלטה">ממתין להחלטה</option>
                      <option value="סגור">סגור</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">עו"ד אחראי / גורם מטפל</label>
                    <select className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 bg-white transition-all" value={formData.lawyer} onChange={e => setFormData({...formData, lawyer: e.target.value})}>
                      <option value="">-- בחר עורך דין --</option>
                      {firmUsers.map(u => <option key={u._id} value={u.name}>{u.name} ({ROLE_NAMES[u.role] || u.role})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">תאריך יעד קרוב</label>
                    <input type="date" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 transition-all" value={formData.deadline} onChange={e => setFormData({...formData, deadline: e.target.value})} />
                  </div>
                </div>
                
                <div className="pt-4 flex justify-end gap-3 border-t border-slate-100 mt-6">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">ביטול</button>
                  <button type="submit" className="px-4 py-2.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"><Save className="w-4 h-4"/> שמור תיק</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
